import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { apiRequest } from '../api/client.js';
import type { ChatHistory, ConnaroEvent } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { ChatMessage } from '../chat/ChatMessage.js';
import { appendEvent, mentionsUser } from '../chat/feed.js';
import { useChatSocket, type ChatConnectionState } from '../chat/use-chat-socket.js';
import './dock.css';

/**
 * OBS dock (ARCHITECTURE.md §24).
 *
 * A Custom Browser Dock inside OBS: a narrow panel an operator watches while
 * running a broadcast. §24's requirements are specific — narrow layout, low
 * overhead, minimal chrome, secure authentication, and **no billing or admin
 * controls**.
 *
 * That last one is satisfied by CONSTRUCTION rather than by hiding things. This
 * component renders a message list and a status line; there is no navigation,
 * no settings, no workspace switcher and no sign-out, so there is nothing
 * dangerous to click by accident with the mouse you are also using to run a
 * stream. A dock built by hiding controls inside the full Console would be one
 * CSS change away from exposing them.
 *
 * It deliberately REUSES the chat feed — same socket hook, same ordering and
 * deduplication, same message renderer, same XSS guarantees (§56). A separate
 * implementation would be a second place for a security property to be wrong.
 */

/** Compact status wording. The dock has room for a word, not a sentence. */
const STATUS: Record<ChatConnectionState, { tone: string; label: string }> = {
  idle: { tone: 'idle', label: 'No workspace' },
  connecting: { tone: 'warn', label: 'Connecting' },
  live: { tone: 'ok', label: 'Live' },
  reconnecting: { tone: 'warn', label: 'Reconnecting' },
  unauthorized: { tone: 'error', label: 'No access' },
  signed_out: { tone: 'error', label: 'Signed out' },
  error: { tone: 'error', label: 'Error' },
};

/**
 * Messages kept in the DOM.
 *
 * Far below the 1,000 the Console buffers. §24 asks for low CPU and GPU
 * overhead, and this runs inside OBS on a machine that is simultaneously
 * encoding video — every DOM node competes with the broadcast. An operator
 * scrolling back through hours of history is a Console activity, not a dock
 * one.
 */
const DOCK_VISIBLE_MESSAGES = 120;

const AUTOSCROLL_THRESHOLD_PX = 40;

export function DockChatPage(): React.JSX.Element {
  const { can, me, activeWorkspace } = useSession();
  const permitted = can('chat.view');

  const workspaceId = permitted ? (activeWorkspace?.id ?? null) : null;
  const socket = useChatSocket(workspaceId);

  const [events, setEvents] = useState<ConnaroEvent[]>([]);
  const listRef = useRef<HTMLOListElement>(null);
  const [pinned, setPinned] = useState(true);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    return socket.onEvent((event) => {
      setEvents((current) => appendEvent(current, event));
    });
  }, [socket]);

  useEffect(() => {
    setEvents([]);
    setUnread(0);
  }, [workspaceId]);

  /* Seed from retained history, so the dock is not blank on open (§31). */
  useEffect(() => {
    if (!permitted || workspaceId === null) return;

    const state = { cancelled: false };

    void (async () => {
      try {
        const history = await apiRequest<ChatHistory>(
          `/chat/messages?limit=${String(DOCK_VISIBLE_MESSAGES)}`,
        );
        if (state.cancelled) return;

        setEvents((current) =>
          history.messages.reduce((acc, event) => appendEvent(acc, event), current),
        );
      } catch {
        // History is a convenience; the live feed is the product.
      }
    })();

    return () => {
      state.cancelled = true;
    };
  }, [permitted, workspaceId]);

  /* Only the tail is rendered — see DOCK_VISIBLE_MESSAGES. */
  const visible = useMemo(() => events.slice(-DOCK_VISIBLE_MESSAGES), [events]);

  const handleScroll = useCallback(() => {
    const list = listRef.current;
    if (list === null) return;

    const atBottom =
      list.scrollHeight - list.scrollTop - list.clientHeight <= AUTOSCROLL_THRESHOLD_PX;

    setPinned(atBottom);
    if (atBottom) setUnread(0);
  }, []);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;

    if (pinned) {
      list.scrollTop = list.scrollHeight;
    } else {
      setUnread((count) => count + 1);
    }
  }, [visible.length, pinned]);

  const jump = useCallback(() => {
    const list = listRef.current;
    if (list === null) return;
    list.scrollTop = list.scrollHeight;
    setPinned(true);
    setUnread(0);
  }, []);

  if (!permitted) {
    return (
      <div className="dock dock--message">
        <p>You do not have access to chat.</p>
      </div>
    );
  }

  const status = STATUS[socket.state];

  return (
    <div className="dock">
      <ol
        className="dock__list"
        ref={listRef}
        onScroll={handleScroll}
        aria-label="Live chat messages"
        aria-live="polite"
        aria-relevant="additions"
      >
        {visible.map((event) => (
          <ChatMessage
            key={event.id}
            event={event}
            mentionHandle={me?.user.displayName}
            mentioned={mentionsUser(event, me?.user.displayName)}
            timeZone={undefined}
          />
        ))}
      </ol>

      {!pinned && unread > 0 && (
        <button type="button" className="dock__jump" onClick={jump}>
          {unread} new ↓
        </button>
      )}

      {visible.length === 0 && (
        <p className="dock__empty">
          {socket.state === 'live' ? 'Waiting for messages…' : 'Connecting to chat…'}
        </p>
      )}

      {/*
       * Status stays visible at all times. §24 requires connection state in the
       * dock, and an operator must be able to tell "chat is quiet" from
       * "Connaro has stopped receiving" at a glance — mid-broadcast, those look
       * identical without it.
       */}
      <footer className={`dock__status dock__status--${status.tone}`}>
        <span className="dock__dot" aria-hidden="true" />
        <span>{status.label}</span>
        {activeWorkspace !== null && (
          <span className="dock__workspace">{activeWorkspace.name}</span>
        )}
      </footer>
    </div>
  );
}
